import {useDataStore} from '@/store/data.store.js'
import {userApi} from '@/api/user.api.js'
import {storeToRefs} from 'pinia'
import {ref, computed} from 'vue'

//Loading flag is global for all composable instances
const isLoadingUser = ref(false)

/**
 * Use current user composable
 */
export function useCurrentUser() {

  //Get store
  const store = useDataStore()
  const {user} = storeToRefs(store)
  const {setUser} = store

  //Load user
  const loadUser = async (force = false) => {

    //Already loaded, we don't need to load again unless forcing to reload
    if (user.value && !force) {
      return
    }

    //Already loading
    if (isLoadingUser.value) {
      return
    }

    //Load user
    try {
      isLoadingUser.value = true
      const loadedUser = await userApi.getCurrent()

      //NOTE: There seems to be a delay between setting via the store setter,
      //and manually setting the value of the ref, which is causing problems
      //for the global resolve hook. Setting the ref value manually for now
      //seems to work around this issue.
      user.value = loadedUser
      setUser(loadedUser)
    }
    finally {
      isLoadingUser.value = false
    }
  }

  //Clear user
  const clearUser = () => setUser(null)

  //Update user
  const updateUser = async data => {
    const user = await userApi.update(data)
    setUser(user)
  }

  //Expose user data as refs
  const userId = computed(() => user.value?.id)
  const externalUserId = computed(() => user.value?.externalUserId)
  const addresses = computed(() => user.value?.addresses)
  const timezone = computed(() => user.value?.timezone)
  const locale = computed(() => user.value?.locale)
  const email = computed(() => user.value?.email)

  //Flags
  const isSuperAdmin = computed(() => user.value?.isSuperAdmin)
  const isAdmin = computed(() => user.value?.isAdmin)
  const hasEmail = computed(() => !!user.value?.email)
  const hasLocale = computed(() => !!user.value?.locale)

  //Expose interface
  return {

    //Data
    user,
    userId,
    externalUserId,
    addresses,
    timezone,
    locale,
    email,

    //Flags
    isLoadingUser,
    isSuperAdmin,
    isAdmin,
    hasEmail,
    hasLocale,

    //Methods
    loadUser,
    clearUser,
    updateUser,
  }
}
